/**
 * Shared API Contract for AGO Finance Frontend
 * Validates and normalizes data before sending to backend
 * Ensures frontend always sends data in the exact format backend expects
 */

// ============================================================================
// VALIDATION PATTERNS (from backend contract)
// ============================================================================

export const ValidationPatterns = {
  phone: /^\d{10}$/,
  pincode: /^\d{6}$/,
  email: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/,
  username: /^[a-zA-Z0-9_]+$/,
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

export const ValidationRules = {
  phone: {
    pattern: ValidationPatterns.phone,
    message: 'Phone number must be exactly 10 digits',
  },
  pincode: {
    pattern: ValidationPatterns.pincode,
    message: 'Pincode must be exactly 6 digits',
  },
  email: {
    pattern: ValidationPatterns.email,
    message: 'Please enter a valid email address',
  },
  password: {
    pattern: ValidationPatterns.password,
    message: 'Password must be 8-16 characters with uppercase, lowercase, number, and special character',
  },
  username: {
    pattern: ValidationPatterns.username,
    minLength: 3,
    maxLength: 20,
    message: 'Username must be 3-20 characters (alphanumeric and underscore only)',
  },
};

// ============================================================================
// ENUM VALUES (from backend contract)
// ============================================================================

export const InvestmentTypes = {
  MUTUAL_FUNDS: 'mutual_funds',
  STOCKS: 'stocks',
  BONDS: 'bonds',
  FIXED_DEPOSITS: 'fixed_deposits',
  REAL_ESTATE: 'real_estate',
  GOLD: 'gold',
  SIP: 'sip',
  FD: 'fd',
  INSURANCE: 'insurance',
  OTHER: 'other',
};

export const PolicyTypes = {
  LIFE: 'life',
  HEALTH: 'health',
  VEHICLE: 'vehicle',
  HOME: 'home',
  TERM: 'term',
  PROPERTY: 'property',
  SENIOR_CITIZEN: 'senior_citizen',
  PERSONAL: 'personal',
  OTHERS: 'others',
};

export const WorkTypes = {
  ASSIGNMENT: 'assignment',
  JOURNAL: 'journal',
  PROJECT: 'project',
  THESIS: 'thesis',
  OTHER: 'other',
};

export const PaymentFrequencies = {
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
  ONE_TIME: 'one-time',
};

export const StudentCategories = {
  SCHOOL: 'School Student',
  COLLEGE_11_12: 'College Student (11th - 12th)',
  UNDERGRAD: 'UnderGrad(Bachlors, Diploma, etc)',
  POSTGRAD: 'PostGrad(Masters,etc)',
};

// ============================================================================
// FIELD VALIDATORS
// ============================================================================

/**
 * Validate a single field
 * @param {string} fieldName - Name of the field
 * @param {any} value - Value to validate
 * @param {object} rules - Validation rules
 * @returns {string|null} - Error message or null if valid
 */
export function validateField(fieldName, value, rules) {
  if (!value && rules.required) {
    return `${fieldName} is required`;
  }

  if (!value) return null; // Optional field, no value provided

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(value)) {
    return rules.message || `${fieldName} is invalid`;
  }

  // Length validation
  if (rules.minLength && value.length < rules.minLength) {
    return `${fieldName} must be at least ${rules.minLength} characters`;
  }

  if (rules.maxLength && value.length > rules.maxLength) {
    return `${fieldName} cannot exceed ${rules.maxLength} characters`;
  }

  // Number validation
  if (rules.minimum !== undefined && Number(value) < rules.minimum) {
    return `${fieldName} must be at least ${rules.minimum}`;
  }

  if (rules.maximum !== undefined && Number(value) > rules.maximum) {
    return `${fieldName} cannot exceed ${rules.maximum}`;
  }

  // Enum validation
  if (rules.enum && !rules.enum.includes(value)) {
    return `${fieldName} must be one of: ${rules.enum.join(', ')}`;
  }

  return null;
}

// ============================================================================
// DATA NORMALIZERS
// ============================================================================

/**
 * Normalize investment data to match backend contract
 */
export function normalizeInvestmentData(formData) {
  const normalized = {
    name: String(formData.name || '').trim(),
    email: String(formData.email || '').toLowerCase().trim(),
    phone: String(formData.phone || '').replace(/\D/g, ''),
    pincode: String(formData.pincode || '').replace(/\D/g, ''),
    investmentType: String(formData.investmentType || '').toLowerCase().replace(/\s+/g, '_'),
    amount: Number(formData.amount || formData.investmentAmount || 0),
    duration: Number(formData.duration || formData.investmentDuration || 0),
  };

  // Optional fields
  if (formData.address) normalized.address = String(formData.address).trim();
  if (formData.whatsapp) normalized.whatsapp = String(formData.whatsapp).replace(/\D/g, '');
  if (formData.age) normalized.age = Number(formData.age);
  if (formData.occupation) normalized.occupation = String(formData.occupation).trim();
  if (formData.paymentFrequency) normalized.paymentFrequency = formData.paymentFrequency;

  return normalized;
}

/**
 * Normalize investment calculate data to match backend contract
 */
export function normalizeInvestmentCalculate(formData) {
  return {
    amount: Number(formData.amount || 0),
    duration: Number(formData.duration || 0),
    investmentType: String(formData.investmentType || 'sip').toLowerCase().replace(/\s+/g, '_'),
  };
}

/**
 * Normalize policy data to match backend contract
 */
export function normalizePolicyData(formData) {
  const normalized = {
    name: String(formData.name || '').trim(),
    email: String(formData.email || '').toLowerCase().trim(),
    phone: String(formData.phone || '').replace(/\D/g, ''),
    pincode: String(formData.pincode || '').replace(/\D/g, ''),
    policyType: String(formData.policyType || '').toLowerCase().replace(/\s+/g, '_'),
  };

  // Optional fields
  if (formData.address) normalized.address = String(formData.address).trim();
  if (formData.whatsapp) normalized.whatsapp = String(formData.whatsapp).replace(/\D/g, '');
  if (formData.customPolicyType) normalized.customPolicyType = String(formData.customPolicyType).trim();
  if (formData.occupation) normalized.occupation = String(formData.occupation).trim();

  return normalized;
}

/**
 * Normalize contact data to match backend contract
 */
export function normalizeContactData(formData) {
  return {
    name: String(formData.name || '').trim(),
    email: String(formData.email || '').toLowerCase().trim(),
    phone: String(formData.phone || '').replace(/\D/g, ''),
    subject: String(formData.subject || '').trim(),
    message: String(formData.message || '').trim(),
  };
}

/**
 * Normalize subscription registration data to match backend contract
 */
export function normalizeSubscriptionData(formData) {
  const normalized = {
    name: String(formData.name || '').trim(),
    email: String(formData.email || '').toLowerCase().trim(),
    phone: String(formData.phone || '').replace(/\D/g, ''),
    address: String(formData.address || '').trim(),
    pincode: String(formData.pincode || '').replace(/\D/g, ''),
    age: Number(formData.age || 0),
    password: String(formData.password || ''),
    studentCategory: String(formData.studentCategory || ''),
    selectedPlan: formData.selectedPlan || {},
  };

  return normalized;
}

/**
 * Normalize OTS (One-Time Service) data to match backend contract
 */
export function normalizeOTSData(formData) {
  const normalized = {
    name: String(formData.name || '').trim(),
    email: String(formData.email || '').toLowerCase().trim(),
    phone: String(formData.phone || '').replace(/\D/g, ''),
    address: String(formData.address || '').trim(),
    workType: String(formData.workType || '').toLowerCase(),
    deadline: formData.deadline, // Keep as-is (ISO date string)
    agreementAccepted: Boolean(formData.agreementAccepted),
  };

  // Optional fields
  if (formData.whatsapp) normalized.whatsapp = String(formData.whatsapp).replace(/\D/g, '');
  if (formData.pages) normalized.pages = Number(formData.pages);
  if (formData.amount) normalized.amount = Number(formData.amount);
  if (formData.additionalInfo) normalized.additionalInfo = String(formData.additionalInfo).trim();
  if (formData.occupation) normalized.occupation = String(formData.occupation).trim();
  if (formData.customWorkType) normalized.customWorkType = String(formData.customWorkType).trim();

  return normalized;
}

/**
 * Normalize workshop application data to match backend contract
 */
export function normalizeWorkshopApplicationData(formData) {
  const normalized = {
    workItemId: formData.workItemId, // must be a valid MongoDB ObjectId string
    name: String(formData.name || '').trim(),
    email: String(formData.email || '').toLowerCase().trim(),
    phone: String(formData.phone || '').replace(/\D/g, ''),
    address: String(formData.address || '').trim(),
    completeOnTime: String(formData.completeOnTime || 'yes'),
    rulesAccepted: Boolean(formData.rulesAccepted),
  };

  // Optional whatsapp field only
  if (formData.whatsapp && formData.whatsapp.trim()) {
    normalized.whatsapp = String(formData.whatsapp).replace(/\D/g, '');
  }

  // Note: sameAsPhone is UI-only and is NOT included in the normalized object
  // Only the fields above are sent to the backend

  return normalized;
}

// ============================================================================
// VALIDATORS
// ============================================================================

/**
 * Validate investment submission data
 * @returns {object} { isValid: boolean, errors: object }
 */
export function validateInvestmentSubmit(data) {
  const errors = {};

  // Required fields
  const nameError = validateField('Name', data.name, { required: true, maxLength: 100 });
  if (nameError) errors.name = nameError;

  const emailError = validateField('Email', data.email, { required: true, pattern: ValidationPatterns.email, message: 'Invalid email format' });
  if (emailError) errors.email = emailError;

  const phoneError = validateField('Phone', data.phone, { required: true, pattern: ValidationPatterns.phone, message: 'Phone must be 10 digits' });
  if (phoneError) errors.phone = phoneError;

  const pincodeError = validateField('Pincode', data.pincode, { required: true, pattern: ValidationPatterns.pincode, message: 'Pincode must be 6 digits' });
  if (pincodeError) errors.pincode = pincodeError;

  const investmentTypeError = validateField('Investment Type', data.investmentType, {
    required: true,
    enum: Object.values(InvestmentTypes),
  });
  if (investmentTypeError) errors.investmentType = investmentTypeError;

  const amountError = validateField('Amount', data.amount, { required: true, minimum: 1000 });
  if (amountError) errors.amount = amountError;

  const durationError = validateField('Duration', data.duration, { required: true, minimum: 1 });
  if (durationError) errors.duration = durationError;

  // Optional fields
  if (data.age) {
    const ageError = validateField('Age', data.age, { minimum: 18, maximum: 100 });
    if (ageError) errors.age = ageError;
  }

  if (data.whatsapp) {
    const whatsappError = validateField('WhatsApp', data.whatsapp, { pattern: ValidationPatterns.phone, message: 'WhatsApp must be 10 digits' });
    if (whatsappError) errors.whatsapp = whatsappError;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate policy submission data
 */
export function validatePolicySubmit(data) {
  const errors = {};

  const nameError = validateField('Name', data.name, { required: true, maxLength: 100 });
  if (nameError) errors.name = nameError;

  const emailError = validateField('Email', data.email, { required: true, pattern: ValidationPatterns.email, message: 'Invalid email format' });
  if (emailError) errors.email = emailError;

  const phoneError = validateField('Phone', data.phone, { required: true, pattern: ValidationPatterns.phone, message: 'Phone must be 10 digits' });
  if (phoneError) errors.phone = phoneError;

  const pincodeError = validateField('Pincode', data.pincode, { required: true, pattern: ValidationPatterns.pincode, message: 'Pincode must be 6 digits' });
  if (pincodeError) errors.pincode = pincodeError;

  const policyTypeError = validateField('Policy Type', data.policyType, {
    required: true,
    enum: Object.values(PolicyTypes),
  });
  if (policyTypeError) errors.policyType = policyTypeError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate contact submission data
 */
export function validateContactSubmit(data) {
  const errors = {};

  const nameError = validateField('Name', data.name, { required: true, maxLength: 100 });
  if (nameError) errors.name = nameError;

  const emailError = validateField('Email', data.email, { required: true, pattern: ValidationPatterns.email, message: 'Invalid email format' });
  if (emailError) errors.email = emailError;

  const phoneError = validateField('Phone', data.phone, { required: true, pattern: ValidationPatterns.phone, message: 'Phone must be 10 digits' });
  if (phoneError) errors.phone = phoneError;

  const subjectError = validateField('Subject', data.subject, { required: true, maxLength: 200 });
  if (subjectError) errors.subject = subjectError;

  const messageError = validateField('Message', data.message, { required: true, maxLength: 2000 });
  if (messageError) errors.message = messageError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate subscription registration data
 */
export function validateSubscriptionRegister(data) {
  const errors = {};

  const nameError = validateField('Name', data.name, { required: true, minLength: 2, maxLength: 100 });
  if (nameError) errors.name = nameError;

  const emailError = validateField('Email', data.email, { required: true, pattern: ValidationPatterns.email, message: 'Invalid email format' });
  if (emailError) errors.email = emailError;

  const phoneError = validateField('Phone', data.phone, { required: true, pattern: ValidationPatterns.phone, message: 'Phone must be 10 digits' });
  if (phoneError) errors.phone = phoneError;

  const addressError = validateField('Address', data.address, { required: true, minLength: 10, maxLength: 500 });
  if (addressError) errors.address = addressError;

  const pincodeError = validateField('Pincode', data.pincode, { required: true, pattern: ValidationPatterns.pincode, message: 'Pincode must be 6 digits' });
  if (pincodeError) errors.pincode = pincodeError;

  const ageError = validateField('Age', data.age, { required: true, minimum: 13, maximum: 100 });
  if (ageError) errors.age = ageError;

  const passwordError = validateField('Password', data.password, { required: true, pattern: ValidationPatterns.password, message: ValidationRules.password.message });
  if (passwordError) errors.password = passwordError;

  const categoryError = validateField('Student Category', data.studentCategory, {
    required: true,
    enum: Object.values(StudentCategories),
  });
  if (categoryError) errors.studentCategory = categoryError;

  if (!data.selectedPlan || !data.selectedPlan.id) {
    errors.selectedPlan = 'Please select a subscription plan';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate OTS submission data
 */
export function validateOTSSubmit(data) {
  const errors = {};

  const nameError = validateField('Name', data.name, { required: true, maxLength: 100 });
  if (nameError) errors.name = nameError;

  const emailError = validateField('Email', data.email, { required: true, pattern: ValidationPatterns.email, message: 'Invalid email format' });
  if (emailError) errors.email = emailError;

  const phoneError = validateField('Phone', data.phone, { required: true, pattern: ValidationPatterns.phone, message: 'Phone must be 10 digits' });
  if (phoneError) errors.phone = phoneError;

  const addressError = validateField('Address', data.address, { required: true, maxLength: 500 });
  if (addressError) errors.address = addressError;

  const workTypeError = validateField('Work Type', data.workType, {
    required: true,
    enum: Object.values(WorkTypes),
  });
  if (workTypeError) errors.workType = workTypeError;

  if (!data.deadline) {
    errors.deadline = 'Deadline is required';
  } else {
    const deadlineDate = new Date(data.deadline);
    if (deadlineDate <= new Date()) {
      errors.deadline = 'Deadline must be a future date';
    }
  }

  if (!data.agreementAccepted) {
    errors.agreementAccepted = 'You must accept the agreement';
  }

  // Conditional validation
  if (data.workType === WorkTypes.ASSIGNMENT && !data.pages) {
    errors.pages = 'Number of pages is required for assignments';
  }

  if (data.workType === WorkTypes.JOURNAL && !data.amount) {
    errors.amount = 'Amount is required for journals';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate workshop application data
 */
export function validateWorkshopApplication(data) {
  const errors = {};

  if (!data.workItemId) {
    errors.workItemId = 'Work item ID is required';
  }

  const nameError = validateField('Name', data.name, { required: true, maxLength: 100 });
  if (nameError) errors.name = nameError;

  const emailError = validateField('Email', data.email, { required: true, pattern: ValidationPatterns.email, message: 'Invalid email format' });
  if (emailError) errors.email = emailError;

  const phoneError = validateField('Phone', data.phone, { required: true, pattern: ValidationPatterns.phone, message: 'Phone must be 10 digits' });
  if (phoneError) errors.phone = phoneError;

  const addressError = validateField('Address', data.address, { required: true, maxLength: 500 });
  if (addressError) errors.address = addressError;

  if (data.completeOnTime === 'no') {
    errors.completeOnTime = 'You must commit to completing the work on time';
  }

  if (!data.rulesAccepted) {
    errors.rulesAccepted = 'You must accept the rules and regulations';
  }

  if (data.whatsapp) {
    const whatsappError = validateField('WhatsApp', data.whatsapp, { pattern: ValidationPatterns.phone, message: 'WhatsApp must be 10 digits' });
    if (whatsappError) errors.whatsapp = whatsappError;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Map frontend display values to backend enum values
 */
export const InvestmentTypeMapper = {
  'Mutual Funds': InvestmentTypes.MUTUAL_FUNDS,
  'Stocks': InvestmentTypes.STOCKS,
  'Bonds': InvestmentTypes.BONDS,
  'Fixed Deposits': InvestmentTypes.FIXED_DEPOSITS,
  'Real Estate': InvestmentTypes.REAL_ESTATE,
  'Gold': InvestmentTypes.GOLD,
  'SIP (Systematic Investment Plan)': InvestmentTypes.SIP,
  'FD (Fixed Deposit)': InvestmentTypes.FD,
  'Insurance': InvestmentTypes.INSURANCE,
};

/**
 * Get backend enum value from frontend display value
 */
export function mapInvestmentType(displayValue) {
  return InvestmentTypeMapper[displayValue] || InvestmentTypes.OTHER;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors) {
  return Object.entries(errors)
    .map(([field, message]) => `• ${message}`)
    .join('\n');
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  ValidationPatterns,
  ValidationRules,
  InvestmentTypes,
  PolicyTypes,
  WorkTypes,
  PaymentFrequencies,
  StudentCategories,
  validateField,
  normalizeInvestmentData,
  normalizePolicyData,
  normalizeContactData,
  normalizeSubscriptionData,
  normalizeOTSData,
  normalizeWorkshopApplicationData,
  validateInvestmentSubmit,
  validatePolicySubmit,
  validateContactSubmit,
  validateSubscriptionRegister,
  validateOTSSubmit,
  validateWorkshopApplication,
  mapInvestmentType,
  formatValidationErrors,
};
